/* eslint-disable @angular-eslint/directive-selector */
import type { AfterViewInit, OnDestroy } from '@angular/core';

import { Directive, ElementRef, Renderer2, inject } from '@angular/core';

const COUNTRY_TOGGLE_SELECTOR = '.iti__selected-flag';
const ACTIVATION_KEYS = ['Enter', ' '];

/**
 * `ngx-intl-tel-input` renders the country selector as a bare `<div dropdownToggle>`, and the ngx-bootstrap
 * directive puts `aria-haspopup` / `aria-expanded` on it. Neither attribute is allowed on the implicit `generic`
 * role of a `<div>`, so Lighthouse fails the `aria-allowed-attr` and `agent-accessibility-tree` audits.
 *
 * Promote the toggle to `role="button"` — the role supports both attributes — and make it operable from the
 * keyboard, since a plain `<div>` reacts to clicks only.
 */
@Directive({
    selector: 'ngx-intl-tel-input',
})
export class SpIntlTelInputA11yDirective implements AfterViewInit, OnDestroy {
    private readonly elementRef = inject<ElementRef<HTMLElement>>(ElementRef);
    private readonly renderer = inject(Renderer2);

    private unlistenKeydown: () => void = null;

    public ngAfterViewInit(): void {
        const toggle = this.elementRef.nativeElement.querySelector(COUNTRY_TOGGLE_SELECTOR);

        if (!toggle) {
            return;
        }

        this.renderer.setAttribute(toggle, 'role', 'button');
        this.renderer.setAttribute(toggle, 'tabindex', '0');

        this.unlistenKeydown = this.renderer.listen(toggle, 'keydown', (event: KeyboardEvent) => {
            if (!ACTIVATION_KEYS.includes(event.key)) {
                return;
            }

            event.preventDefault();
            (event.currentTarget as HTMLElement).click();
        });
    }

    public ngOnDestroy(): void {
        this.unlistenKeydown?.();
    }
}
